import * as THREE from 'three';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';

import { VBOLoader } from '../Loader/VBOLoader.js';
import Grid from '../Geodata/grid.js';
import FVector from '@data/geometry/FVector.js';
import { VisualizationElementId } from '@rendering/types/VisualizationElements';

const loader = new VBOLoader();
const modelsPath = 'assets/models/';

export type VisualizationElement = {
    path: string;
    id: VisualizationElementId;
    name: string;
    geometry: THREE.InstancedBufferGeometry;
    scaleFactor?: number;
    showStroke: boolean;
    visible?: boolean;
};
export class VisualizationElements {
    static readonly geometries: VisualizationElement[] = [
        {
            path: '',
            id: VisualizationElementId.Quad,
            name: 'Quad',
            geometry: createQuadGeometry(),
            scaleFactor: 1,
            showStroke: true,
            visible: true,
        },
        {
            path: '',
            id: VisualizationElementId.Voxel,
            name: 'Voxel',
            geometry: createBoxGeometry(),
            showStroke: true,
            visible: true,
        },
        {
            path: '',
            id: VisualizationElementId.FlatVoxel,
            name: 'Flat Voxel',
            geometry: createBoxGeometry(true),
            scaleFactor: 0.6,
            showStroke: true,
            visible: false,
        },
        {
            path: '',
            id: VisualizationElementId.Point,
            name: 'Point',
            geometry: createSphereGeometry(),
            scaleFactor: 0.3,
            showStroke: false,
            visible: true,
        },
        {
            path: '',
            id: VisualizationElementId.Box,
            name: 'Box',
            geometry: createBoxGeometry(),
            showStroke: true,
            visible: true,
        },
    ];
    static selectionBox?: THREE.Object3D;

    static getGeometry(ID: VisualizationElementId) {
        const entry = VisualizationElements.geometries.find((geometry) => geometry.id === ID);
        if (!entry) {
            throw new Error(`Unknown visualization element id: ${ID}`);
        }
        if (!entry.geometry) {
            // This can happen if the geometry is supposed to be loaded asynchronously and loadGeometries() hasn't finished yet
            throw new Error(
                `Geometry for id ${ID} is not loaded yet. Make sure loadGeometries() has completed before calling getGeometry.`
            );
        }
        return entry.geometry;
    }

    static getVisualizationElement(ID: VisualizationElementId): VisualizationElement {
        const entry = VisualizationElements.geometries.find((visualizationElement) => visualizationElement.id === ID);
        if (!entry) {
            throw new Error(`Unknown visualization element id: ${ID}`);
        }
        if (!entry.geometry) {
            // This can happen if the geometry is supposed to be loaded asynchronously and loadGeometries() hasn't finished yet
            throw new Error(
                `Geometry for id ${ID} is not loaded yet. Make sure loadGeometries() has completed before calling getGeometry.`
            );
        }
        return entry;
    }

    static getShowStroke(ID: VisualizationElementId) {
        const entry = VisualizationElements.geometries.find((geometry) => geometry.id === ID);
        if (!entry) {
            throw new Error(`Unknown visualization element id: ${ID}`);
        }
        return entry.showStroke;
    }

    static async loadGeometries(scene: THREE.Scene) {
        try {
            for (let i = 0; i < VisualizationElements.geometries.length; i++) {
                const geometryInfo = VisualizationElements.geometries[i];
                if (geometryInfo.path == '') continue;
                const geometryPath = modelsPath + geometryInfo.path;
                geometryInfo.geometry = await loader.load(geometryPath);
                let scale = geometryInfo.scaleFactor;
                if (scale != null) geometryInfo.geometry.scale(scale, scale, scale);
            }
            await VisualizationElements.loadSelectedBorder(scene);
        } catch (error) {
            console.error('Error loading assets: ', error);
        }
        // console.log(this.geometries[2].geometry);
    }

    static async loadSelectedBorder(scene: THREE.Scene) {
        let boxGeom = await loader.load(modelsPath + 'SelBorder.obj');
        boxGeom.scale(Grid.gridRes * 1.001, Grid.gridRes * 1.001, Grid.gridRes * 1.001);

        // FillMesh (camera is upclose):
        const fillMaterial = new THREE.MeshBasicMaterial({
            color: new THREE.Color(0, 0, 0),
            side: THREE.DoubleSide,
        });
        const fillMesh = new THREE.Mesh(boxGeom.clone(), fillMaterial);

        // LineSegments (for when the camera is far):
        const edgesGeometry = new THREE.EdgesGeometry(boxGeom);
        const lineGeometry = new LineSegmentsGeometry();
        const edgePositions = edgesGeometry.getAttribute('position').array as ArrayLike<number>;
        lineGeometry.setPositions(Array.from(edgePositions));

        const lineMaterial = new LineMaterial({
            color: new THREE.Color(0, 0, 0),
            linewidth: 2.5,
        });

        const selectionLine = new LineSegments2(lineGeometry, lineMaterial);

        // Create & add Group (Fill + Stroke) to the scene:
        const selectionGroup = new THREE.Group();
        selectionGroup.add(fillMesh, selectionLine);
        selectionGroup.rotation.set(0, 0, THREE.MathUtils.degToRad(Grid.gridAngle));
        VisualizationElements.selectionBox = selectionGroup;
        VisualizationElements.selectionBox.name = 'selected_element_box';

        scene.add(VisualizationElements.selectionBox);
    }

    static moveSelectedBorder(position: FVector, densityScale: number = 1) {
        if (!VisualizationElements.selectionBox) return;
        VisualizationElements.selectionBox.position.set(position.x, position.y, position.z);

        // scale relative to the base (which is already Grid.gridRes*1.001)
        VisualizationElements.selectionBox.scale.set(densityScale, densityScale, densityScale);
    }

    static drawSelectedBorder(show: boolean) {
        if (!VisualizationElements.selectionBox) return;
        VisualizationElements.selectionBox.visible = show;
    }
}

function createBoxGeometry(flat?: boolean): THREE.InstancedBufferGeometry {
    const baseGeometry = new THREE.BoxGeometry(1, 1, 1);
    if (flat) baseGeometry.scale(1, 1, 0);
    const instancedGeometry = new THREE.InstancedBufferGeometry();

    instancedGeometry.index = baseGeometry.index;
    instancedGeometry.setAttribute('position', baseGeometry.getAttribute('position'));
    instancedGeometry.setAttribute('normal', baseGeometry.getAttribute('normal'));
    instancedGeometry.setAttribute('uv', baseGeometry.getAttribute('uv'));

    const coloredFaceAttribute = new THREE.BufferAttribute(new Float32Array(baseGeometry.attributes.position.count), 1);
    const faceIndex = 4; // front face
    for (let i = 0; i < 4; i++) {
        coloredFaceAttribute.setX(faceIndex * 4 + i, 1);
    }
    instancedGeometry.setAttribute('coloredFace', coloredFaceAttribute);

    return instancedGeometry;
}

function createQuadGeometry(): THREE.InstancedBufferGeometry {
    const geometry = new THREE.InstancedBufferGeometry();

    // Define the 4 corner positions (counter-clockwise)
    const vertices = new Float32Array([
        -0.5,
        -0.5,
        0, // bottom left
        0.5,
        -0.5,
        0, // bottom right
        0.5,
        0.5,
        0, // top right
        -0.5,
        0.5,
        0, // top left
        0,
        -0.0001,
        0,
        0,
        0,
        0.5, // arrow tip
        0,
        0.0001,
        0,
    ]);

    // Define the 2 triangles using vertex indices
    const indices = [
        0,
        1,
        2, // first triangle
        2,
        3,
        0, // second triangle
        4,
        5,
        6, // arrow triangle
    ];

    geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals(); // optional, for lighting

    return geometry;
}

function createSphereGeometry(): THREE.InstancedBufferGeometry {
    const baseGeometry = new THREE.SphereGeometry(0.25, 16, 16);
    const instancedGeometry = new THREE.InstancedBufferGeometry();

    instancedGeometry.index = baseGeometry.index;
    instancedGeometry.setAttribute('position', baseGeometry.getAttribute('position'));
    instancedGeometry.setAttribute('normal', baseGeometry.getAttribute('normal'));
    instancedGeometry.setAttribute('uv', baseGeometry.getAttribute('uv'));

    return instancedGeometry;
}
